import { NgTemplateOutlet } from "@angular/common";
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild
} from "@angular/core";
import { NavigationEnd, Event as RouterEvent, Router, RouterLink } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { filter } from "rxjs";
import { NzDividerComponent } from "ng-zorro-antd/divider";

import { LktIconComponent } from "../icon/icon.component";
import { LktIconName } from "../icon/icon.registry";
import { LktUserPopoverComponent } from "../user-popover/user-popover.component";
import { UserPopoverItem } from "../user-popover/user-popover-item.interface";
import { LktViewSwitchComponent } from "../view-switch/view-switch.component";
import { ViewSwitchSegment } from "../view-switch/view-switch.type";
import { NavbarMenuItemType } from "../../types/navbar-menu-item.type";

const RINGING_CLASS = "is-ringing";

/**
 * Rail de navegação da linguagem Lekto V3 (icon-only, com tooltip no hover),
 * replicando o `.sidebar` / `.adm-sidebar` do protótipo lekto-v3.
 *
 * - Itens simples navegam por rota (estado ativo por match de URL).
 * - Itens com `hasGroup` abrem um flyout lateral com os subitens (rotas internas
 *   ou links externos `http(s)`).
 * - `showViewSwitch` renderiza o alternador Administração ⇄ Plataforma.
 * - `showNotifications` renderiza o sino, com contador (`notificationsUnreadLabel`), semântica de
 *   popup (`aria-haspopup`/`aria-expanded`) e toque na chegada (`notificationsRingToken`); emite
 *   `notificationsClick` com o próprio botão, para o painel se ancorar.
 * - O usuário é exibido via `lkt-user-popover` (nome oculto no rail via CSS).
 *
 * `menuItems[].icon` e `helpMenuItems[].icon` devem usar nomes do registro de
 * ícones (`LktIconName`, ex.: "home", "book", "help").
 */
@Component({
  selector: "lkt-sidebar-menu",
  standalone: true,
  imports: [
    NgTemplateOutlet,
    RouterLink,
    NzDividerComponent,
    LktIconComponent,
    LktViewSwitchComponent,
    LktUserPopoverComponent
  ],
  templateUrl: "./sidebar-menu.component.html",
  styleUrl: "./sidebar-menu.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LktSidebarMenuComponent {
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  // Layout / marca
  public readonly isCollapsed = input<boolean>(false);
  public readonly logoSrc = input<string>("logo-primary.svg");
  public readonly logoAlt = input<string>("Logo");
  public readonly logoRoute = input<string>("/");

  // Dados de navegação
  public readonly menuItems = input<NavbarMenuItemType[]>([]);
  public readonly helpMenuItems = input<NavbarMenuItemType[]>([]);
  public readonly userPopoverItems = input<UserPopoverItem[]>([]);

  // Usuário
  public readonly userName = input<string>("Usuário");
  public readonly userAvatar = input<string>("");
  public readonly profileLabel = input<string>("Usuário");
  public readonly profileIcon = input<string>("");
  public readonly hasMultipleProfiles = input<boolean>(false);
  public readonly version = input<string>("");

  // Alternador de sistema (Administração ⇄ Plataforma)
  public readonly showViewSwitch = input<boolean>(false);
  public readonly viewSwitchSegments = input<ViewSwitchSegment[]>([]);
  public readonly viewSwitchActiveId = input<string>("");

  // Notificações (DEV-872)
  public readonly showNotifications = input<boolean>(false);
  /** Já formatado pelo estado: "" | "1".."9" | "9+". O corte do 9+ não é decisão do rail. */
  public readonly notificationsUnreadLabel = input<string>("");
  public readonly notificationsAriaLabel = input<string>("Notificações");
  public readonly notificationsOpen = input<boolean>(false);
  /** Incrementa a cada notificação nova. Mudança de valor reinicia o toque do sino. */
  public readonly notificationsRingToken = input<number>(0);

  // Compatibilidade legada (não usada no rail; mantida para não quebrar bindings)
  public readonly crossAppLabel = input<string>("");
  public readonly crossAppIcon = input<string>("");
  public readonly showCrossAppButton = input<boolean>(false);

  public readonly toggleSidebar = output<void>();
  public readonly popoverItemClick = output<string>();
  public readonly switchProfileClick = output<void>();
  public readonly helpMenuExpand = output<void>();
  public readonly userPopoverVisibleChange = output<boolean>();
  public readonly crossAppClick = output<void>();
  public readonly viewSwitchClick = output<string>();
  /** Emite o próprio botão: é ele que o painel usa para se ancorar. */
  public readonly notificationsClick = output<HTMLElement>();

  public readonly bellIcon: LktIconName = "bell";

  private readonly currentUrl = signal<string>("");
  public readonly openGroupId = signal<string | null>(null);
  public readonly userPopoverOpen = signal<boolean>(false);
  // `read: ElementRef` é obrigatório aqui: `#bellIconRef` está num elemento de componente
  // (`<lkt-icon>`), e sem essa opção a query resolveria para a instância do LktIconComponent,
  // não para o ElementRef do seu nó nativo. O overload com `read` exige DOIS parâmetros de tipo
  // (`<LocatorT, ReadT>`) — com um só, o TS cai no overload sem `read` (propriedade desconhecida,
  // TS2353) e o retorno vira `{}` (`.nativeElement` inexistente, TS2339). Mesmo padrão de
  // `notifications-panel.container.ts:panelRef`.
  private readonly bellIconRef = viewChild<LktIconComponent, ElementRef<HTMLElement>>(
    "bellIconRef",
    { read: ElementRef }
  );

  constructor() {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeTransientPanels();
      });

    // O reinício do toque é imperativo de propósito: remover e readicionar a classe no mesmo frame
    // é coalescido pelo browser e a animação não recomeça. Precisa da leitura de layout no meio.
    afterRenderEffect(() => {
      if (this.notificationsRingToken() === 0) {
        return;
      }
      this.restartBellRing();
    });
  }

  public readonly hasHelp = computed<boolean>(() => this.helpMenuItems().length > 0);

  public iconName(icon: string | undefined): LktIconName {
    return (icon ?? "") as LktIconName;
  }

  public isItemActive(item: NavbarMenuItemType): boolean {
    if (item.hasGroup) {
      return (item.routeGroup ?? []).some((sub) => this.isRouteActive(sub.route));
    }
    if (this.isRouteActive(item.route)) {
      return true;
    }

    // Aditivo: `matchRoutes` é opcional, então quem não passa o campo mantém exatamente o
    // destaque por `route`. Ver NavbarMenuItemType.
    return (item.matchRoutes ?? []).some((route) => this.isRouteActive(route));
  }

  public isSubItemActive(route: string | undefined): boolean {
    return this.isRouteActive(route);
  }

  public isExternal(route: string | undefined): boolean {
    return !!route && /^https?:\/\//i.test(route);
  }

  public toggleGroup(id: string): void {
    this.setUserPopoverOpen(false);
    this.openGroupId.update((current) => (current === id ? null : id));
    this.helpMenuExpand.emit();
  }

  /**
   * Toda navegação disparada de dentro do rail (logo, item simples, subitem interno ou externo)
   * recolhe os painéis. Não pode depender de `NavigationEnd`: clique no link da rota atual emite
   * `NavigationSkipped`, e navegação barrada por guard emite `NavigationCancel`.
   */
  public onNavigate(): void {
    this.closeTransientPanels();
  }

  public onViewSwitchClick(id: string): void {
    this.closeTransientPanels();
    this.viewSwitchClick.emit(id);
  }

  public onNotificationsClick(event: MouseEvent): void {
    this.closeTransientPanels();
    // `currentTarget` é o próprio botão durante o dispatch — não depende de viewChild resolvido.
    const bell = event.currentTarget as HTMLElement | null;
    if (bell) {
      this.notificationsClick.emit(bell);
    }
  }

  /**
   * Reinicia a animação do sino. Público para ser testável sem depender do flush de
   * `afterRenderEffect`. Só o ícone gira: o badge fica parado.
   *
   * A preferência de movimento é consultada no momento da exibição (CA 08), para respeitar mudança
   * feita com a aplicação já aberta.
   */
  public restartBellRing(): void {
    if (this.prefersReducedMotion()) {
      return;
    }
    const icon = this.bellIconRef()?.nativeElement;
    if (!icon) {
      return;
    }
    icon.classList.remove(RINGING_CLASS);
    // Leitura de layout forçada: é ela que separa o remove do add em dois frames de estilo.
    void icon.offsetWidth;
    icon.classList.add(RINGING_CLASS);
  }

  private prefersReducedMotion(): boolean {
    // Guarda de ambiente no mesmo padrão do IntersectionObserver na infinite-scroll.directive.
    if (typeof globalThis.matchMedia === "undefined") {
      return false;
    }
    return globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  public onCloseSidebar(): void {
    this.toggleSidebar.emit();
  }

  public onPopoverItemClick(itemId: string): void {
    this.popoverItemClick.emit(itemId);
  }

  public onSwitchProfile(): void {
    this.switchProfileClick.emit();
  }

  public onUserPopoverVisibleChange(visible: boolean): void {
    if (visible) {
      this.openGroupId.set(null);
    }
    this.setUserPopoverOpen(visible);
  }

  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent): void {
    if (this.openGroupId() === null) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.openGroupId.set(null);
    }
  }

  /**
   * Ponto único de escrita de `userPopoverOpen`. A guarda evita reemitir o output quando o
   * fechamento chega pelos dois caminhos do mesmo clique: o outside-click do overlay do CDK e o
   * reset explícito de quem abriu o outro painel.
   */
  private setUserPopoverOpen(visible: boolean): void {
    if (this.userPopoverOpen() === visible) {
      return;
    }
    this.userPopoverOpen.set(visible);
    this.userPopoverVisibleChange.emit(visible);
  }

  private closeTransientPanels(): void {
    this.openGroupId.set(null);
    this.setUserPopoverOpen(false);
  }

  private isRouteActive(route: string | undefined): boolean {
    if (!route || this.isExternal(route)) {
      return false;
    }
    const current = this.normalize(this.currentUrl());
    const target = route.startsWith("/") ? route : `/${route}`;
    if (target === "/") {
      return current === "/" || current === "";
    }
    return current === target || current.startsWith(`${target}/`);
  }

  private normalize(url: string): string {
    const clean = url.split("?")[0].split("#")[0];
    return clean.endsWith("/") && clean !== "/" ? clean.slice(0, -1) : clean;
  }
}
